import type {ReactElement} from 'react';
import {useState} from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, PermissionsProvider} from '@/components';
import type {AdminBusinessInvoice} from '@/types';

import InvoiceStatusModal from './InvoiceStatusModal';

type InvoiceStatusButtonProps = {
  invoice: AdminBusinessInvoice;
  /** Called after a successful update so the list refreshes in place. */
  onSuccess?: () => void | Promise<void>;
  /** Test hook for the button; `Button` suffixes it with `-button`. */
  dataCy?: string;
  /** Test hook prefix for the modal's fields. */
  formDataCy?: string;
};

/**
 * Changing an invoice's payment state is the same class of action as raising
 * one, so it rides on the invoice create permission rather than carrying its
 * own.
 */
const InvoiceStatusButton = ({
  invoice,
  onSuccess,
  dataCy,
  formDataCy,
}: InvoiceStatusButtonProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);

  return (
    <PermissionsProvider
      permission={AfriexAdminPermission.BusinessInvoiceManage}
    >
      {isOpen ? (
        <InvoiceStatusModal
          isOpen={isOpen}
          onClose={toggle}
          invoice={invoice}
          onSuccess={onSuccess}
          formDataCy={formDataCy}
        />
      ) : (
        <Button
          variant="unStyled"
          colorScheme="cyan"
          onClick={toggle}
          dataCy={dataCy}
        >
          Update status
        </Button>
      )}
    </PermissionsProvider>
  );
};

export default InvoiceStatusButton;
